/**
 * ChatTransferPairing.ts
 *
 * Pure-function QR codec for the chat-transfer pairing handshake.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatTransferPairing.swift` — same prefix
 * scheme `kaether:chat-transfer:v1:`, same 32-byte X25519 ephemeral public key
 * field, same 5-minute freshness window. Two web-specific deltas vs. iOS:
 *
 *   - The QR transports `senderEphemeralPub` rather than a receiver pub. The
 *     web↔web direction matches the textual spec in `Signal_standard_web.md`
 *     (K3); the cryptographic relationship is symmetric — both endpoints
 *     derive the same `transferKey` regardless of which side's ephemeral pub
 *     rides on the QR.
 *   - The QR carries a 16-byte `nonce` instead of a Bonjour service id. Bonjour
 *     is not addressable from a browser; the transport will be WebRTC, and the
 *     nonce binds a particular QR to a particular session so a captured QR
 *     cannot be redeemed twice. The 16-byte width is generous against the
 *     birthday bound for a short-lived 5-minute QR.
 *   - `createdAt` is unix **milliseconds** (web convention) rather than
 *     seconds. The freshness window is still 5 minutes; clock-skew tolerance
 *     toward the future is 30 seconds.
 *
 * Wire format:
 *
 *   kaether:chat-transfer:v1:<base64url(senderEphemeralPub[32] ‖ nonce[16] ‖ createdAtMs-big-endian-u64[8])>
 *
 * Total payload size before base64url: 56 bytes. base64url-no-pad expands to
 * 75 characters, plus the 25-character prefix → ~100 characters of QR
 * plaintext. Well within QR Code Version 5-L (~134 bytes alphanumeric) so the
 * code stays scannable from typical screen-to-camera distances.
 *
 * No DOM dependencies. No SolidJS imports. The QR-rendering and camera-scan
 * surfaces live in a separate UI file.
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

/**
 * Decoded chat-transfer QR payload.
 *
 * The QR carries deliberately limited information:
 *   - schema/prefix gates: `kaether:chat-transfer:v1:` (encoded in the prefix)
 *   - 32-byte X25519 **ephemeral** public key (NEVER an identity key — the
 *     KDF derivation chain is bound only to ephemeral material)
 *   - 16-byte random nonce so a captured QR cannot be redeemed under a
 *     different session
 *   - createdAt (unix ms) — 5-minute freshness window
 */
export interface ChatTransferQRPayload {
  /** 32-byte X25519 raw public key generated fresh per transfer. */
  readonly senderEphemeralPub: Uint8Array;
  /** 16-byte randomly generated nonce binding this QR to one session. */
  readonly nonce: Uint8Array;
  /** Unix milliseconds at which the QR was generated. */
  readonly createdAt: number;
}

/**
 * Discriminated error union for QR codec failures.
 *
 * `kind` strings are stable so the UI can switch on them without parsing
 * free-text messages.
 */
export type ChatTransferPairingErrorKind =
  | "missingPrefix"
  | "invalidBase64"
  | "wrongPayloadLength"
  | "invalidPublicKeyLength"
  | "invalidNonceLength"
  | "stalePayload"
  | "futureSkew";

export class ChatTransferPairingError extends Error {
  readonly kind: ChatTransferPairingErrorKind;
  /** Age in milliseconds (set only for `stalePayload`). */
  readonly ageMs?: number;
  /** Skew toward the future in milliseconds (set only for `futureSkew`). */
  readonly skewMs?: number;

  constructor(
    kind: ChatTransferPairingErrorKind,
    message: string,
    extra?: { ageMs?: number; skewMs?: number },
  ) {
    super(message);
    this.name = "ChatTransferPairingError";
    this.kind = kind;
    this.ageMs = extra?.ageMs;
    this.skewMs = extra?.skewMs;
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** QR plaintext prefix gate. */
export const QR_PREFIX = "kaether:chat-transfer:v1:";

export const SENDER_EPHEMERAL_PUB_BYTES = 32;
export const NONCE_BYTES = 16;
export const CREATED_AT_BYTES = 8;
export const QR_PAYLOAD_BYTES =
  SENDER_EPHEMERAL_PUB_BYTES + NONCE_BYTES + CREATED_AT_BYTES; // 56

/** 5-minute freshness window for the QR. Matches iOS. */
export const QR_MAX_AGE_MS = 5 * 60_000;

/** Tolerance for receiver clocks that are slightly ahead of the sender. */
export const QR_FUTURE_SKEW_MS = 30_000;

// ---------------------------------------------------------------------------
// base64url helpers (no padding)
// ---------------------------------------------------------------------------

function toBase64url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64url(str: string): Uint8Array {
  // Reject any characters outside the base64url alphabet up front — without
  // this guard the call to `atob` silently accepts a surprising amount of
  // garbage by ignoring characters it does not recognise.
  if (!/^[A-Za-z0-9_-]*$/.test(str)) {
    throw new ChatTransferPairingError(
      "invalidBase64",
      "QR payload contains characters outside the base64url alphabet",
    );
  }
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (padded.length % 4)) % 4;
  const base64 = padded + "=".repeat(padLen);
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    throw new ChatTransferPairingError(
      "invalidBase64",
      "QR payload is not valid base64url",
    );
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ---------------------------------------------------------------------------
// Big-endian uint64 helpers for createdAt
// ---------------------------------------------------------------------------

function writeBigEndianU64(bytes: Uint8Array, offset: number, value: number): void {
  // JavaScript numbers are safe up to 2^53; unix-ms today is ~1.7e12, well
  // within that bound and the BigInt-free path keeps the code small.
  let hi = Math.floor(value / 0x1_0000_0000);
  let lo = value >>> 0;
  bytes[offset + 0] = (hi >>> 24) & 0xff;
  bytes[offset + 1] = (hi >>> 16) & 0xff;
  bytes[offset + 2] = (hi >>> 8) & 0xff;
  bytes[offset + 3] = hi & 0xff;
  bytes[offset + 4] = (lo >>> 24) & 0xff;
  bytes[offset + 5] = (lo >>> 16) & 0xff;
  bytes[offset + 6] = (lo >>> 8) & 0xff;
  bytes[offset + 7] = lo & 0xff;
}

function readBigEndianU64(bytes: Uint8Array, offset: number): number {
  const hi =
    bytes[offset + 0] * 0x100_0000 +
    (bytes[offset + 1] << 16) +
    (bytes[offset + 2] << 8) +
    bytes[offset + 3];
  const lo =
    bytes[offset + 4] * 0x100_0000 +
    (bytes[offset + 5] << 16) +
    (bytes[offset + 6] << 8) +
    bytes[offset + 7];
  return hi * 0x1_0000_0000 + lo;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Cheap structural check — does this string look like a chat-transfer QR?
 *
 * Returns false for every sibling protocol prefix (`kaether:link:v1:`,
 * `kaether:msg:v1:`, etc.). Use `decodeQR` to surface validation errors.
 */
export function isChatTransferQR(s: string): boolean {
  return s.startsWith(QR_PREFIX);
}

/**
 * Encode a payload to its canonical QR string.
 *
 * Deterministic: the same inputs produce the same string, byte-for-byte.
 * Throws synchronously on shape errors (wrong-length keys/nonces, negative
 * createdAt). Freshness is NOT checked here — the caller decides when to
 * generate the QR.
 */
export function encodeQR(payload: ChatTransferQRPayload): string {
  if (payload.senderEphemeralPub.length !== SENDER_EPHEMERAL_PUB_BYTES) {
    throw new ChatTransferPairingError(
      "invalidPublicKeyLength",
      `senderEphemeralPub must be ${SENDER_EPHEMERAL_PUB_BYTES} bytes`,
    );
  }
  if (payload.nonce.length !== NONCE_BYTES) {
    throw new ChatTransferPairingError(
      "invalidNonceLength",
      `nonce must be ${NONCE_BYTES} bytes`,
    );
  }
  if (!Number.isFinite(payload.createdAt) || payload.createdAt < 0) {
    throw new ChatTransferPairingError(
      "wrongPayloadLength",
      "createdAt must be a non-negative finite number",
    );
  }
  const buf = new Uint8Array(QR_PAYLOAD_BYTES);
  buf.set(payload.senderEphemeralPub, 0);
  buf.set(payload.nonce, SENDER_EPHEMERAL_PUB_BYTES);
  writeBigEndianU64(buf, SENDER_EPHEMERAL_PUB_BYTES + NONCE_BYTES, payload.createdAt);
  return QR_PREFIX + toBase64url(buf);
}

/**
 * Decode a QR string back to the payload.
 *
 * Validates, in order:
 *   - the prefix
 *   - base64url shape and total decoded byte length (56)
 *   - length of senderEphemeralPub (32) and nonce (16)
 *   - createdAt freshness (`now - createdAt <= QR_MAX_AGE_MS`)
 *   - createdAt future skew (`createdAt - now <= QR_FUTURE_SKEW_MS`)
 *
 * `now` defaults to `Date.now()` but is injectable so tests can pin the clock.
 */
export function decodeQR(
  s: string,
  now: number = Date.now(),
): ChatTransferQRPayload {
  if (!s.startsWith(QR_PREFIX)) {
    throw new ChatTransferPairingError(
      "missingPrefix",
      `QR must start with "${QR_PREFIX}"`,
    );
  }
  const encoded = s.slice(QR_PREFIX.length);
  const decoded = fromBase64url(encoded);
  if (decoded.length !== QR_PAYLOAD_BYTES) {
    throw new ChatTransferPairingError(
      "wrongPayloadLength",
      `QR payload must be ${QR_PAYLOAD_BYTES} bytes, got ${decoded.length}`,
    );
  }
  const senderEphemeralPub = decoded.slice(0, SENDER_EPHEMERAL_PUB_BYTES);
  const nonce = decoded.slice(
    SENDER_EPHEMERAL_PUB_BYTES,
    SENDER_EPHEMERAL_PUB_BYTES + NONCE_BYTES,
  );
  // These structural checks are belt-and-suspenders: a 56-byte input always
  // slices into 32+16+8, but a future refactor that bumps QR_PAYLOAD_BYTES
  // without bumping the slice widths would silently truncate. Keep the
  // explicit length asserts so a typo surfaces at the codec edge.
  if (senderEphemeralPub.length !== SENDER_EPHEMERAL_PUB_BYTES) {
    throw new ChatTransferPairingError(
      "invalidPublicKeyLength",
      `senderEphemeralPub decoded to ${senderEphemeralPub.length} bytes`,
    );
  }
  if (nonce.length !== NONCE_BYTES) {
    throw new ChatTransferPairingError(
      "invalidNonceLength",
      `nonce decoded to ${nonce.length} bytes`,
    );
  }
  const createdAt = readBigEndianU64(
    decoded,
    SENDER_EPHEMERAL_PUB_BYTES + NONCE_BYTES,
  );

  const age = now - createdAt;
  if (age > QR_MAX_AGE_MS) {
    throw new ChatTransferPairingError(
      "stalePayload",
      `QR is ${age}ms old; max age is ${QR_MAX_AGE_MS}ms`,
      { ageMs: age },
    );
  }
  if (createdAt - now > QR_FUTURE_SKEW_MS) {
    throw new ChatTransferPairingError(
      "futureSkew",
      `QR createdAt is ${createdAt - now}ms in the future; tolerance is ${QR_FUTURE_SKEW_MS}ms`,
      { skewMs: createdAt - now },
    );
  }
  return { senderEphemeralPub, nonce, createdAt };
}

/**
 * Convenience: returns `null` for any input that fails validation or is
 * simply not a chat-transfer QR. Use `decodeQR` directly when the UI needs
 * to surface the specific error kind.
 */
export function tryDecodeQR(
  s: string,
  now: number = Date.now(),
): ChatTransferQRPayload | null {
  if (!isChatTransferQR(s)) return null;
  try {
    return decodeQR(s, now);
  } catch {
    return null;
  }
}
